import React, { useMemo } from "react";
import { Building2, Target, Users, Shield, Factory, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
// images served from public/ should be referenced via absolute paths
const BASE = import.meta.env.BASE_URL || "/";
const image1 = BASE + "NSPObuilding03.webp";
const image2 = BASE + "logo.webp";
const image3 = BASE + "assets/images/hero.webp";
const image4 = BASE + "assets/images/hero.webp";
// Define the content structure with icons, colors, and images
const detailedContent = {
  ar: {
    title: "جهاز مشروعات الخدمة الوطنية",
    subtitle: "التاريخ والأهداف الاستراتيجية",
    sections: [
      {
        id: "establishment",
        title: "نشأة جهاز مشروعات الخدمة الوطنية",
        icon: Building2,
        color: "from-emerald-500 to-teal-600",
        image: image2,
        items: [
          "أُنشئ الجهاز بالقرار الجمهورى رقم 32 لعام 1979 والمعدل بالقرار رقم 583 لعام 1980",
          "للجهاز شخصية إقتصادية إعتبارية وله ميزانية مستقلة عن ميزانية القوات المسلحة وتخضع لمراجعة الجهاز المركزى للمحاسبات بالدولة",
        ],
      },
      {
        id: "objectives",
        title: "الأهداف الرئيسية",
        icon: Target,
        color: "from-blue-500 to-cyan-600",
        image: image3,
        items: [
          {
            subtitle: "الإكتفاء الذاتى",
            description:
              "تحقيق الإكتفاء الذاتى النسبى من الإحتياجات الرئيسية للقوات المسلحة لتخفيف أعباء تدبيرها عن كاهل الدولة مع طرح فائض الطاقات الإنتاجية للشركات / القطاعات بالسوق المحلى",
            icon: Shield,
            image: image4,
            subItems: null,
          },
          {
            subtitle: "معاونة مشروعات التنمية",
            description:
              "معاونة مشروعات التنمية الإقتصادية بالدولة لتحقيق الآتى:",
            icon: Factory,
            image: image4,
            subItems: [
              "زيادة العائد القومى بالدولة مع التركيز على الأصناف الإستراتيجية",
              "التعاون مع أجهزة الدولة والقطاع المدنى فى إقامة المشروعات الإقتصادية المختلفة بهدف زيادة الدخل القومى والسيطرة على معدلات التضخم مع التركيز على مشروعات البنية الأساسية وإعطاء عناية خاصة لإقامة مشروعات تنموية بمحافظات سيناء والمحافظات الحدودية وجنوب الوادى بما يساعد على تنمية هذه المحافظات وجذب الإستثمارات إليها",
              "العمل على منع الممارسات الإحتكارية بتوفير إحتياجات المواطنين الأساسية من السلع والخدمات بأسعار مناسبة",
              "خلق فرص عمل لشباب الخريجين بالمشروعات التى ينفذها الجهاز ، بالإضافة إلى تدريب وتأهيل الشباب لخلق كوادر متخصصة من العمالة فى مجالات العمل المختلفة",
            ],
          },
        ],
      },
    ],
  },
  en: {
    title: "National Service Projects Organization",
    subtitle: "History and Strategic Objectives",
    sections: [
      {
        id: "establishment",
        title: "Establishment of the National Service Projects Organization",
        icon: Building2,
        color: "from-emerald-500 to-teal-600",
        image: image2,
        items: [
          "Established by Presidential Decree No. 32 of 1979 and amended by Decree No. 583 of 1980",
          "The organization has an independent legal economic entity and a budget separate from the Armed Forces budget, subject to audit by the Central Auditing Organization.",
        ],
      },
      {
        id: "objectives",
        title: "Main Objectives",
        icon: Target,
        color: "from-blue-500 to-cyan-600",
        image: image1,
        items: [
          {
            subtitle: "Self-Sufficiency",
            description:
              "Achieving relative self-sufficiency in the main needs of the Armed Forces to reduce the state's burden, while making surplus production capacities available in the local market.",
            icon: Shield,
            image: image1,
            subItems: null,
          },
          {
            subtitle: "Assisting Development Projects",
            description:
              "Assisting economic development projects in the state to achieve the following:",
            icon: Factory,
            image: image2,
            subItems: [
              "Increasing the national return with a focus on strategic items.",
              "Cooperating with state agencies and the civil sector to establish economic projects aimed at increasing national income and controlling inflation, focusing on infrastructure and development projects in Sinai, border, and Southern Valley governorates.",
              "Working to prevent monopolistic practices by providing citizens' basic needs for goods and services at appropriate prices.",
              "Creating job opportunities for graduate youth in the projects, in addition to training and qualifying youth to create specialized labor cadres.",
            ],
          },
        ],
      },
    ],
  },
  fr: {
    title: "Organisation des Projets de Service National",
    subtitle: "Histoire et Objectifs Stratégiques",
    sections: [
      {
        id: "establishment",
        title: "Création de l'Organisation des Projets de Service National",
        icon: Building2,
        color: "from-emerald-500 to-teal-600",
        image: image1,
        items: [
          "Créée par le décret présidentiel n° 32 de 1979 et modifiée par le décret n° 583 de 1980",
          "L'organisation a une personnalité économique légale indépendante avec un budget distinct de celui des Forces armées, soumis au contrôle de l'Organisation centrale d'audit.",
        ],
      },
      {
        id: "objectives",
        title: "Objectifs Principaux",
        icon: Target,
        color: "from-blue-500 to-cyan-600",
        image: image2,
        items: [
          {
            subtitle: "Autosuffisance",
            description:
              "Atteindre une autosuffisance relative pour les besoins principaux des Forces armées pour alléger la charge de l'État, tout en mettant les capacités de production excédentaires à disposition sur le marché local.",
            icon: Shield,
            image: image2,
            subItems: null,
          },
          {
            subtitle: "Soutien aux Projets de Développement",
            description:
              "Aider les projets de développement économique de l'État à atteindre les objectifs suivants :",
            icon: Factory,
            image: image3,
            subItems: [
              "Accroître le rendement national en mettant l'accent sur les articles stratégiques.",
              "Coopérer avec les agences étatiques et le secteur civil pour établir des projets économiques visant à augmenter le revenu national et à contrôler l'inflation, en se concentrant sur les projets d'infrastructure et les projets de développement dans le Sinaï, les régions frontalières et la Vallée du Sud.",
              "Travailler à prévenir les pratiques monopolistiques en fournissant aux citoyens leurs besoins fondamentaux en biens et services à des prix appropriés.",
              "Créer des opportunités d'emploi pour les jeunes diplômés dans les projets, en plus de former et de qualifier les jeunes pour créer des cadres spécialisés de main-d'œuvre.",
            ],
          },
        ],
      },
    ],
  },
};

const AboutDetails = () => {
  const { i18n } = useTranslation("common");
  const { language } = useStore();

  const currentLanguage = language || i18n.language || "ar";

  // Use useMemo to get the current content and direction efficiently
  const currentContent = useMemo(
    () => detailedContent[currentLanguage] || detailedContent.ar,
    [currentLanguage]
  );
  const isRTL = currentLanguage === "ar";

  const sections = currentContent.sections;

  // Additional data for the summary cards below the main sections
  const summaryCards = {
    ar: [
      {
        icon: Factory,
        title: "التنمية المستدامة",
        description: "دعم مشروعات البنية الأساسية والتنمية الاقتصادية",
        color: "from-emerald-500 to-teal-600",
        image: image1,
      },
      {
        icon: Users,
        title: "خلق فرص العمل",
        description: "تدريب وتأهيل الشباب وتوفير فرص عمل للخريجين",
        color: "from-blue-500 to-cyan-600",
        image: image2,
      },
      {
        icon: Shield,
        title: "الإكتفاء الذاتى",
        description: "تحقيق الاكتفاء الذاتي من الاحتياجات الأساسية",
        color: "from-emerald-500 to-teal-600",
        image: image3,
      },
    ],
    en: [
      {
        icon: Factory,
        title: "Sustainable Development",
        description:
          "Supporting infrastructure and economic development projects",
        color: "from-emerald-500 to-teal-600",
        image: image1,
      },
      {
        icon: Users,
        title: "Job Creation",
        description:
          "Training and qualifying youth and providing job opportunities for graduates",
        color: "from-blue-500 to-cyan-600",
        image: image2,
      },
      {
        icon: Shield,
        title: "Self-Sufficiency",
        description: "Achieving self-sufficiency in basic needs",
        color: "from-emerald-500 to-teal-600",
        image: image4,
      },
    ],
    fr: [
      {
        icon: Factory,
        title: "Développement Durable",
        description:
          "Soutien aux projets d'infrastructure et de développement économique",
        color: "from-emerald-500 to-teal-600",
        image: image4,
      },
      {
        icon: Users,
        title: "Création d'Emplois",
        description:
          "Formation et qualification des jeunes et création d'opportunités d'emploi",
        color: "from-blue-500 to-cyan-600",
        image: image3,
      },
      {
        icon: Shield,
        title: "Autosuffisance",
        description: "Atteindre l'autosuffisance pour les besoins de base",
        color: "from-emerald-500 to-teal-600",
        image: image2,
      },
    ],
  };

  const currentSummaryCards = useMemo(
    () => summaryCards[currentLanguage],
    [currentLanguage]
  );

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 ${
        isRTL ? "rtl" : "ltr"
      }`}
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* Header */}
      <header className="bg-white shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-3">
              <Globe className="w-6 h-6 text-indigo-600" />
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-800">
                {currentContent.title}
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Main Title Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 mb-6 shadow-xl">
              <Building2 className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-800 mb-4">
              {currentContent.title}
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {currentContent.subtitle}
            </p>
          </div>

          {/* Dynamic Sections */}
          <div className="space-y-12">
            {sections.map((section, sectionIdx) => (
              <div
                key={section.id}
                className="bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:shadow-3xl"
              >
                {/* Section Header Bar */}
                <div className={`bg-gradient-to-r ${section.color} p-6 sm:p-8`}>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-inner">
                      <section.icon className="w-7 h-7 text-white" />
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white">
                      {section.title}
                    </h2>
                  </div>
                </div>

                {/* Section Image */}
                {section.image && (
                  <div className="h-64 md:h-80 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                    <img
                      src={section.image}
                      alt={section.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}

                {/* Section Content: Flat List or Nested Objectives */}
                <div className="p-6 sm:p-8">
                  {/* Check if it's the simple list (Establishment) */}
                  {section.id === "establishment" ? (
                    <div className="space-y-6">
                      {section.items.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex gap-4 p-5 bg-slate-50 rounded-xl border-r-4 border-emerald-500 hover:bg-slate-100 transition-colors duration-200 shadow-sm"
                        >
                          <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm mt-1">
                            {idx + 1}
                          </div>
                          <p className="text-slate-700 text-lg leading-relaxed flex-1">
                            {item}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Nested Objectives List */
                    <div className="space-y-10">
                      {section.items.map((obj, idx) => (
                        <div
                          key={idx}
                          className="bg-gradient-to-br from-white to-slate-100 p-6 rounded-xl border border-slate-200 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden"
                        >
                          {/* Sub-Objective Header & Description */}
                          <div className="flex items-start gap-4 mb-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center shadow-md">
                              <obj.icon className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-slate-800 mb-2">
                                {obj.subtitle}
                              </h3>
                              <p className="text-slate-700 leading-relaxed">
                                {obj.description}
                              </p>
                            </div>
                          </div>

                          {/* Nested Sub-Items */}
                          {obj.subItems && (
                            <div
                              className={`mt-6 space-y-3 ${
                                isRTL ? "mr-14" : "ml-14"
                              }`}
                            >
                              {obj.subItems.map((subItem, subIdx) => (
                                <div
                                  key={subIdx}
                                  className="flex gap-3 items-start group"
                                >
                                  <div className="flex-shrink-0 w-2 h-2 bg-cyan-500 rounded-full mt-2.5 group-hover:scale-125 transition-transform duration-200"></div>
                                  <p className="text-slate-600 leading-relaxed flex-1">
                                    {subItem}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Summary Cards */}
          <div className="mt-16 text-center">
            <h2 className="text-3xl font-extrabold text-slate-800 mb-8 border-b-2 border-indigo-200 inline-block pb-1">
              {isRTL ? "ملخص الأثر" : "Summary Impact"}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {currentSummaryCards.map((card, index) => (
              <div
                key={index}
                className="group bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <div
                  className={`h-40 overflow-hidden bg-gradient-to-br from-white to-slate-100`}
                >
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 text-center">
                  <div
                    className={`w-14 h-14 bg-gradient-to-br ${card.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}
                  >
                    <card.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">
                    {card.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{card.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AboutDetails;
